import { useCallback, useEffect, useRef, useState } from "react";
import type { ImageCropSession } from "@/components/common/ImageCropModal";

/** Opens ImageCropModal for a file; resolves with the cropped File or null if cancelled. */
export function useImageCropSession() {
  const [session, setSession] = useState<ImageCropSession | null>(null);
  const resolverRef = useRef<((file: File | null) => void) | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      if (resolverRef.current) {
        resolverRef.current(null);
        resolverRef.current = null;
      }
    };
  }, []);

  const requestImageCrop = useCallback((file: File): Promise<File | null> => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    const imageSrc = URL.createObjectURL(file);
    blobUrlRef.current = imageSrc;
    return new Promise((resolve) => {
      resolverRef.current = (result) => {
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
          blobUrlRef.current = null;
        }
        resolverRef.current = null;
        setSession(null);
        resolve(result);
      };
      setSession({ imageSrc, filename: file.name });
    });
  }, []);

  const resolveCrop = useCallback((file: File | null) => {
    resolverRef.current?.(file);
  }, []);

  return { session, requestImageCrop, resolveCrop };
}
